<template>
  <v-app>
    <!-- Premium Header with Glass Effect -->
    <v-app-bar
      color="linear-gradient(135deg, #0A1929 0%, #1A2F42 100%)"
      elevation="0"
      height="90"
      v-if="!route.path.includes('client-segment-analytics')"
      class="header-app-bar"
    >
      <v-container fluid class="header-container">
        <!-- Back Button (only shown on non-root pages) -->
        <button 
          v-if="!isRootPage" 
          class="back-button" 
          @click="goBack"
          :title="$t('common.back')"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="back-icon">
            <path d="M12 16l-6-6 6-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <!-- Logo with Subtle Shadow -->
        <div class="logo-wrapper" @click="goHome">
          <div class="logo-shadow"></div>
          <img 
            class="onevr-logo" 
            src="/assets/oneVRDarkBackLogo.png" 
            :alt="$t('homepage.header.logoAlt')" 
          />
        </div>

        <v-spacer></v-spacer>

        <!-- Connect with us Button -->
        <button class="connect-button-header" @click="goToContact">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" class="button-icon">
            <path d="M2 6l7 5 7-5M4 15h10a2 2 0 002-2V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="button-text">{{ $t('homepage.header.connectButton') }}</span>
        </button>

        <!-- Beautiful Language Toggle -->
        <div class="language-toggle-container">
          <div class="language-toggle-elegant">
            <button
              :class="['lang-button', { active: currentLanguage === 'en' }]"
              @click="setLanguage('en')"
            >
              <div class="lang-button-inner">
                <img src="/assets/usa_flag.svg" alt="EN" class="flag-icon" />
                <span class="lang-label">English</span>
              </div>
              <div class="button-glow"></div>
            </button>
            
            <button
              :class="['lang-button', { active: currentLanguage === 'de' }]"
              @click="setLanguage('de')"
            >
              <div class="lang-button-inner">
                <img src="/assets/german_flag.svg" alt="DE" class="flag-icon" />
                <span class="lang-label">Deutsch</span>
              </div>
              <div class="button-glow"></div>
            </button>
            
            <div class="toggle-slider" :style="sliderStyle"></div>
          </div>
        </div>
      </v-container>
    </v-app-bar>

    <!-- Main Content with padding for fixed app bar -->
    <v-main class="main-content">
      <router-view />
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

const { locale, t } = useI18n();
const router = useRouter();
const route = useRoute();
const currentLanguage = ref('en');

// Check if current page is root
const isRootPage = computed(() => {
  return route.path === '/' || route.name === 'Home';
});

// Computed slider style for language toggle
const sliderStyle = computed(() => ({
  transform: currentLanguage.value === 'en' ? 'translateX(0)' : 'translateX(100%)'
}));

// Methods
const setLanguage = (lang) => {
  locale.value = lang;
  currentLanguage.value = lang;
};

const goBack = () => {
  router.back();
};

const goHome = () => {
  if (route.path !== '/') {
    router.push('/');
  }
};

const goToContact = () => {
  router.push({ name: 'ContactUs' });
};

onMounted(async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() === 'android') {
      // Android without edge-to-edge: env(safe-area-inset-*) returns 0.
      // Manually set CSS vars for status bar (top) and nav bar (bottom).
      document.documentElement.style.setProperty('--android-status-bar', '24px');
      document.documentElement.style.setProperty('--android-nav-bar', '48px');
    }
  } catch {
    // running on web/iOS, no action needed
  }
});
</script>

<style scoped>
/* Global App Styles */
html,
body {
  background-color: #0A1929;
  margin: 0;
  padding: 0;
}

/* Main Content */
.main-content {
  padding-top: calc(var(--header-height, 90px) + var(--android-status-bar, env(safe-area-inset-top, 0px)));
  padding-bottom: calc(var(--android-nav-bar, env(safe-area-inset-bottom, 0px)));
}

/* ==================== Dark Header with Glass Effect ==================== */
.header-app-bar {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000;
  background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%) !important;
  height: calc(var(--header-height, 90px) + var(--android-status-bar, env(safe-area-inset-top, 0px))) !important;
  padding-top: var(--android-status-bar, env(safe-area-inset-top, 0px)) !important;
  padding-left: env(safe-area-inset-left, 0px) !important;
  padding-right: env(safe-area-inset-right, 0px) !important;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  align-items: center;
  height: 100%;
  gap: 1rem;
}

/* ==================== Back Button ==================== */
.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.back-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), transparent);
  transition: left 0.5s ease;
}

.back-button:hover::before {
  left: 100%;
}

.back-button:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%);
  border-color: #3b82f6;
  transform: translateX(-4px) scale(1.05);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
}

.back-button:active {
  transform: translateX(-2px) scale(1);
}

.back-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Logo Styling */
.logo-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
}

.logo-shadow {
  position: absolute;
  inset: -4px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.logo-wrapper:hover .logo-shadow {
  opacity: 1;
}

.onevr-logo {
  height: 65px;
  width: auto;
  object-fit: contain;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08));
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.logo-wrapper:hover .onevr-logo {
  transform: scale(1.05);
}

/* ==================== Connect Button in Header ==================== */
.connect-button-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  margin-right: 20px;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
}

.connect-button-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.connect-button-header:hover::before {
  left: 100%;
}

.connect-button-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.connect-button-header:active {
  transform: translateY(0);
}

.connect-button-header .button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==================== Beautiful Language Toggle ==================== */
.language-toggle-container {
  display: flex;
  align-items: center;
}

.language-toggle-elegant {
  position: relative;
  display: flex;
  gap: 8px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
  padding: 6px;
  border-radius: 16px;
  box-shadow: 
    0 4px 12px rgba(59, 130, 246, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 0.5),
    0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.15);
}

.toggle-slider {
  position: absolute;
  top: 6px;
  left: 6px;
  width: calc(50% - 10px);
  height: calc(100% - 12px);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 
    0 4px 12px rgba(59, 130, 246, 0.3),
    0 2px 6px rgba(59, 130, 246, 0.2);
  z-index: 0;
}

.lang-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  height: 48px;
  padding: 0 20px;
  background: transparent;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
  overflow: hidden;
}

.lang-button-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 2;
}

.flag-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.lang-button:hover .flag-icon {
  transform: scale(1.1) rotate(-5deg);
}

.lang-label {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: color 0.3s ease;
  color: #64748b;
}

.lang-button.active .lang-label {
  color: #ffffff;
}

.lang-button:not(.active) .lang-label {
  color: #64748b;
}

.lang-button:not(.active):hover .lang-label {
  color: #475569;
}

.button-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 12px;
  z-index: 0;
}

.lang-button:hover .button-glow {
  opacity: 1;
}

.lang-button.active {
  color: #ffffff;
}

/* ==================== Responsive Design ==================== */
@media (max-width: 1024px) {
  .header-container {
    padding: 0 2rem;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 0 1.5rem;
    gap: 0.75rem;
  }

  .onevr-logo {
    height: 55px;
  }

  .back-button {
    width: 40px;
    height: 40px;
  }

  .connect-button-header {
    padding: 10px 16px;
    font-size: 14px;
    margin-right: 12px;
  }

  .button-text {
    display: none;
  }

  .language-toggle-elegant {
    padding: 5px;
  }

  .lang-button {
    min-width: 85px;
    height: 42px;
    padding: 0 14px;
  }

  .flag-icon {
    width: 22px;
    height: 22px;
  }

  .lang-label {
    font-size: 14px;
  }

  .main-content {
    padding-top: calc(75px + var(--android-status-bar, env(safe-area-inset-top, 0px)));
  }

  .header-app-bar {
    height: calc(75px + var(--android-status-bar, env(safe-area-inset-top, 0px))) !important;
    padding-top: var(--android-status-bar, env(safe-area-inset-top, 0px)) !important;
  }
}

@media (max-width: 600px) {
  .lang-label {
    display: none;
  }

  .lang-button {
    min-width: 50px;
    padding: 0 12px;
  }

  .toggle-slider {
    width: calc(50% - 8px);
  }
}

@media (max-width: 480px) {
  .header-container {
    padding: 0 1rem;
    gap: 0.5rem;
  }

  .onevr-logo {
    height: 45px;
  }

  .back-button {
    width: 36px;
    height: 36px;
  }

  .back-icon {
    width: 18px;
    height: 18px;
  }

  .connect-button-header {
    padding: 8px 12px;
    min-width: 40px;
    margin-right: 8px;
  }

  .lang-button {
    min-width: 45px;
    height: 36px;
    padding: 0 10px;
  }

  .flag-icon {
    width: 20px;
    height: 20px;
  }
}

/* ==================== Accessibility ==================== */
.back-button:focus-visible,
.connect-button-header:focus-visible,
.lang-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.logo-wrapper:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 4px;
  border-radius: 8px;
}
</style>